// CRO6684 mapping notes:
// - Maldives charter: existing OfferPage charterBundleRoute signal.
// - All-inclusive: offer/property holiday type tag "All-inclusive" (primary), else
//   all-inclusive dining category/text on every available Flash package,
//   or boardCode allinclusive / all-inclusive text on every available rate-backed path.
// - Breakfast included: breakfast inclusion text, hidden when All-inclusive matches.
//   Rate-level boardCode breakfast is not shown.
// - Adults only: every available package/room capacity disallows children, infants,
//   and teenagers, and the property Adult age definition is 18+ (not 13+).
// - Adults-only area: explicit adults-only area text without a structured
//   adults-only property flag.
// - Massage included: explicit massage inclusion text.
// - Kids stay free, Dining credit, Daily cocktails, Late checkout:
//   standard package inclusion categories from inclusionsV2 on every available package/rate.
//   Late checkout is hidden on charter bundle offers.
// - Nightly drink: nightly drink inclusion text, hidden when Daily cocktails matches.
// - Great for couples: couple occupancy + Romantic/Honeymoon travel filter holiday type + not adults-only.
// - Romantic Escape heading: same Romantic/Honeymoon holiday type requirement; couple occupancy alone
//   falls back to the default heading.
// - Family friendly: family occupancy + family/kid facility/holiday text + not adults-only.
// - Highly rated by travellers: review count >= 5 and rating >= 8.8 on the app's 10-point scale.
// - Ski-in/ski-out: disabled until ski lift access flag or distance to nearest ski lift <= 500m is available.
// - Beach nearby: distance to nearest beach <= 1km.
// - Atlas Beach Club: package inclusion text.
// - CANNA Bali Beach Club Access: package inclusion text, hidden when Atlas Beach Club matches.
// - Central location: enough general nearby POIs within 3km.
// - Great location: restaurant/cafe + attraction POI counts within 1.5km.
// - Walkable neighbourhood: restaurant/cafe + attraction POI counts within 500m.
//   TODO(CRO6684): add shop POI category when supported by the surroundings API.
// - Arts & culture nearby: museum/gallery/theatre/cultural POI within 2km.
// - Return airport transfers: explicit return/round-trip transfer inclusion text.
// - Golf nearby: golf POI within 10km.
// - Easy airport access: disabled until drive time to nearest major airport <= 45 minutes is available.
// - Close to transport: transit/station POI within 800m.
// - TODO P16 - Ocean-view rooms: room amenity/feature view text.
//   TODO(CRO6684): use structured room inventory view values.
// - TODO P17 - Spacious rooms: minimum available room size >= 35sqm.
//   Flash hotel has structured package room size; property pages parse room.size text;
//   bedbank uses room feature dimensions.
//   TODO(CRO6684): use normalised room size sqm across offer types and minimum
//   room-type sample when available.
// - TODO P18 - Interconnecting rooms: family/group occupancy + 3 or more guests + interconnecting room text.
//   TODO(CRO6684): use structured interconnecting-room availability.
// - Free WiFi: free/complimentary Wi-Fi text.
// - TODO P21 - 24-hour reception: 24-hour front desk/reception text.
//   TODO(CRO6684): use structured front desk hours.
// - TODO P22 - Express check-in: express check-in text.
//   TODO(CRO6684): use structured express check-in service flag.
// - Parking: standard package inclusion category.
// - Airport transfers: structured facility names only, hidden when return airport transfers matches.
// - Onsite dining: structured facility names only.
// - Bar & lounge onsite: structured facility names only.
// - Room service: room service text.
// - Indoor pool: indoor pool text.
// - Kids pool: kids/children's pool text + not adults-only.
// - Fitness centre: gym/fitness text.
// - Spa & wellness facilities: spa/wellness/sauna text, hidden when massage included matches.
// - Accessible facilities: disabled for now.
export const NIGHTLY_DRINK_INCLUSION_REGEX = /\bnightly\b[\s\S]*?\b(?:drinks?|beverages?)\b/i

export const OFFER_PAGE_HIGHLIGHT_TAG_IDS = {
  MALDIVES_CHARTER: 'maldives-charter',
  ALL_INCLUSIVE: 'all-inclusive',
  BREAKFAST_INCLUDED: 'breakfast-included',
  ADULTS_ONLY: 'adults-only',
  ADULTS_ONLY_AREA: 'adults-only-area',
  MASSAGE_INCLUDED: 'massage-included',
  KIDS_STAY_FREE: 'kids-stay-free',
  DINING_CREDIT: 'dining-credit',
  DAILY_COCKTAILS: 'daily-cocktails',
  NIGHTLY_DRINK: 'nightly-drink',
  LATE_CHECKOUT: 'late-checkout',
  GREAT_FOR_COUPLES: 'great-for-couples',
  FAMILY_FRIENDLY: 'family-friendly',
  HIGHLY_RATED: 'highly-rated',
  SKI_IN_SKI_OUT: 'ski-in-ski-out',
  BEACH_NEARBY: 'beach-nearby',
  ATLAS_BEACH_CLUB: 'atlas-beach-club',
  CANNA_BALI_BEACH_CLUB_ACCESS: 'canna-bali-beach-club-access',
  CENTRAL_LOCATION: 'central-location',
  GREAT_LOCATION: 'great-location',
  WALKABLE_NEIGHBOURHOOD: 'walkable-neighbourhood',
  ARTS_CULTURE_NEARBY: 'arts-culture-nearby',
  RETURN_AIRPORT_TRANSFERS: 'return-airport-transfers',
  GOLF_NEARBY: 'golf-nearby',
  EASY_AIRPORT_ACCESS: 'easy-airport-access',
  CLOSE_TO_TRANSPORT: 'close-to-transport',
  OCEAN_VIEW_ROOMS: 'ocean-view-rooms',
  SPACIOUS_ROOMS: 'spacious-rooms',
  INTERCONNECTING_ROOMS: 'interconnecting-rooms',
  FREE_WIFI: 'free-wifi',
  RECEPTION: 'reception',
  EXPRESS_CHECK_IN: 'express-check-in',
  PARKING: 'parking',
  AIRPORT_TRANSFERS: 'airport-transfers',
  ONSITE_DINING: 'onsite-dining',
  BAR_LOUNGE: 'bar-lounge',
  ROOM_SERVICE: 'room-service',
  INDOOR_POOL: 'indoor-pool',
  KIDS_POOL: 'kids-pool',
  FITNESS_CENTRE: 'fitness-centre',
  SPA_WELLNESS: 'spa-wellness',
  ACCESSIBLE_FACILITIES: 'accessible-facilities',
  FAMILY_POOLS: 'family-pools',
  FAMILY_LOCATION: 'family-location',
  FAMILY_DINING: 'family-dining',
  FAMILY_SPACE: 'family-space',
  COUPLE_SUNSET: 'couple-sunset',
  COUPLE_COCKTAILS: 'couple-cocktails',
  COUPLE_RESORT: 'couple-resort',
  COUPLE_SERVICE: 'couple-service',
  DEFAULT_RATING: 'default-rating',
  DEFAULT_LOCATION: 'default-location',
  DEFAULT_PERKS: 'default-perks',
  DEFAULT_SETTING: 'default-setting',
} as const

export type OfferPageHighlightTagId = typeof OFFER_PAGE_HIGHLIGHT_TAG_IDS[keyof typeof OFFER_PAGE_HIGHLIGHT_TAG_IDS]

export type OfferPageHighlightTagIcon =
  | 'bed-sofa'
  | 'car'
  | 'cloch'
  | 'clock'
  | 'dumbbell'
  | 'family'
  | 'glass-martini'
  | 'golf'
  | 'grapes'
  | 'heart-card'
  | 'kid'
  | 'map-marker'
  | 'parking'
  | 'plane'
  | 'restaurant'
  | 'shop'
  | 'ski-lift'
  | 'snow-flake'
  | 'star'
  | 'sunset'
  | 'swimmer'
  | 'theatre'
  | 'trees'
  | 'utensils'
  | 'walking'
  | 'water'
  | 'wellness'
  | 'wheelchair'
  | 'wifi'

export const ARTS_CULTURE_POI_TERMS = [
  'art gallery',
  'arts centre',
  'arts center',
  'cultural centre',
  'cultural center',
  'gallery',
  'museum',
  'opera',
  'theatre',
  'theater',
]

export const GOLF_POI_TERMS = [
  'golf',
  'golf course',
]

export const TRANSPORT_POI_TERMS = [
  'bus station',
  'bus stop',
  'metro station',
  'rail station',
  'railway station',
  'subway station',
  'train station',
  'tram stop',
  'transit station',
]
